import { useState, useEffect, useContext } from 'react';
import { format, isPast, startOfMonth } from 'date-fns';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BookingDialog from '../../../components/dialogs/bookingDialog/BookingDialog';
import {
    AppContext,
    SttTopControls,
    SttTable,
    SttChipUser,
    SttPage,
} from '../../../sporttia/all';
import { useInteractionsFiles, useToggle } from '../../../lib/hooks';
import { formatPriceByLocale, fullName } from '../../../lib/utils';
import DateBuilder from '../../../lib/DateBuilder';

type Filters = {
    name: string;
    ini: string;
    end: string;
    status?: string;
    idFacility?: number;
    source?: string;
    dni?: string;
};

type Booking = {
    id: number;
    created: string;
    creator: any;
    user: any;
    ini: string;
    end: string;
    facility: any;
    price: number;
    totalBill?: number;
    totalPaid: number;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type BookingsResponse = {
    rows: Booking[];
    count: number;
};

export default function Bookings(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const { downloadFile } = useInteractionsFiles();
    const [filters, setFilters] = useState<Filters>({
        name: '',
        ini: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        end: format(new Date(), 'yyyy-MM-dd'),
    });
    const [bookings, setBookings] = useState<BookingsResponse>();
    const [bookingId, setBookingId] = useState<number | null>(null);

    function loadBookings(params = { page: 1, rows: 20 }) {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/bookings`, {
            params: {
                ...params,
                ...filters,

                trash,
            },
            success: (result: BookingsResponse) => {
                setBookings(result);
            },
        });
    }

    // Trash hook
    const [trash, toggleTrash] = useToggle(() => {
        loadBookings();
    });

    // set page title
    useEffect(() => {
        cxt?.setHeader({ title: t('bookings') });
    }, []);

    /**
     * Download booking PDF
     */
    function download(fileFormat: string) {
        const type = fileFormat === 'pdf' ? 'application/pdf' : 'text/csv';
        downloadFile(
            `/scs/${cxt?.sc?.id}/bookings.${fileFormat}`,
            type,
            { page: 1, ...filters },
            `${t('Bookings')} ${filters.ini
                .split('-')
                .reverse()
                .join('-')} - ${filters.end
                .split('-')
                .reverse()
                .join('-')}.${fileFormat}`,
        );
    }

    // Render
    return (
        <SttPage title={t('bookings')}>
            <SttTopControls
                p={2}
                fields={[
                    {
                        caption: t('Name'),
                        name: 'name',
                        type: 'text',
                        value: filters.name,
                        onEnterKey: () => loadBookings(),
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                extraFields={[
                    {
                        caption: t('Status'),
                        name: 'status',
                        type: 'select',
                        value: filters.status || '',
                        options: (cxt?.constants?.paymentStatuses || []).map(
                            (opt: any) => ({
                                label: opt.label,
                                value: opt.id,
                            }),
                        ),
                        mandatory: false,
                        cleanable: true,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                    {
                        caption: t('Facility'),
                        name: 'idFacility',
                        type: 'selectAsync',
                        value: filters.idFacility,
                        labelField: 'name',
                        valueField: 'id',
                        loader: () =>
                            new Promise((resolve: any, reject: any) => {
                                cxt?.api(
                                    'GET',
                                    `/scs/${cxt?.sc?.id}/facilities`,
                                    {
                                        params: {
                                            page: 1,
                                            rows: 1000,
                                            status: 'ACTIVE,ONLY_SC',
                                        },
                                        success: (response: any) =>
                                            resolve(response.rows),
                                        error: (response: any) =>
                                            reject(response.msg),
                                    },
                                );
                            }),
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                    {
                        caption: t('Source'),
                        name: 'source',
                        type: 'select',
                        value: filters.source || '',
                        options: (cxt?.constants?.bookingSources || []).map(
                            (opt: any) => ({
                                label: opt.label,
                                value: opt.id,
                            }),
                        ),
                        mandatory: false,
                        cleanable: true,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                    {
                        caption: t('Begin'),
                        name: 'ini',
                        type: 'date',
                        value: filters.ini,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                    {
                        caption: t('End'),
                        name: 'end',
                        type: 'date',
                        value: filters.end,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                    {
                        caption: `${t('dni')}/CIF`,
                        name: 'dni',
                        type: 'text',
                        value: filters.dni || '',
                        onEnterKey: () => loadBookings(),
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                menu={[
                    {
                        caption: `${t('Download')} PDF`,
                        onClick: () => download('pdf'),
                    },
                    {
                        caption: `${t('Download')} CSV`,
                        onClick: () => download('csv'),
                    },
                ]}
                menuIcon={<CloudDownloadIcon />}
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: any;
                }) => {
                    if (!name) return;
                    // Handle date formatting for ini and end fields
                    if ((name === 'ini' || name === 'end') && value) {
                        setFilters({
                            ...filters,
                            [name]: format(new Date(value), 'yyyy-MM-dd'),
                        });
                    } else {
                        setFilters({ ...filters, [name]: value });
                    }
                }}
                onFilter={() => loadBookings()}
                onToggleTrash={toggleTrash}
            />

            <SttTable
                data={bookings}
                loading={false}
                columns={[
                    {
                        title: t('Created'),
                        value: (row: Booking) => (
                            <>
                                <Box className="mb-2">
                                    {new DateBuilder(
                                        row?.created,
                                        cxt?.sc?.timezone?.name,
                                        cxt?.lang?.key,
                                    ).dmy()}
                                </Box>
                                {'creator' in row &&
                                    row.creator &&
                                    (row.user &&
                                    row.creator.id === row.user.id ? (
                                        <SttChipUser user={row.user} />
                                    ) : (
                                        <SttChipUser user={row.creator} />
                                    ))}
                            </>
                        ),
                    },
                    {
                        field: 'name',
                        title: t('User'),
                        align: 'left',
                        value: (row: Booking) => (
                            <Link
                                component="button"
                                variant="body2"
                                underline="hover"
                                onClick={() => setBookingId(row.id)}
                                sx={{
                                    textAlign: 'left',
                                    color: 'var(--brand-text)',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                }}
                            >
                                {fullName(row.user)}
                            </Link>
                        ),
                    },
                    {
                        title: t('Date'),
                        value: (row: Booking) => {
                            const bookingDate = new Date(row.ini);
                            const isBookingPast = isPast(bookingDate);
                            return (
                                <>
                                    <Box
                                        className={
                                            isBookingPast
                                                ? 'text-destructive'
                                                : undefined
                                        }
                                    >
                                        {new DateBuilder(
                                            row.ini,
                                            cxt?.sc?.timezone?.name,
                                            cxt?.lang?.key,
                                        ).dmy()}
                                    </Box>
                                    <Box
                                        className={
                                            isBookingPast
                                                ? 'text-destructive'
                                                : 'text-gray-500'
                                        }
                                    >
                                        {format(bookingDate, 'H:mm')} -{' '}
                                        {format(new Date(row.end), 'H:mm')}
                                    </Box>
                                </>
                            );
                        },
                    },
                    {
                        title: t('Facility'),
                        type: 'facility',
                        field: 'facility',
                    },
                    {
                        field: 'debt',
                        align: 'right',
                        type: 'debt',
                        title: t('Debt'),
                        value: (row: Booking) =>
                            (row.totalBill ?? row.price) - row.totalPaid,
                    },
                    {
                        field: 'price',
                        align: 'right',
                        type: 'price',
                        title: t('Total'),
                        value: (row: Booking) =>
                            formatPriceByLocale(
                                row.totalBill ?? row.price,
                                row.priceShape?.currency,
                                row.priceShape?.locale,
                            ),
                    },
                ]}
                onFetch={loadBookings}
            />

            {bookingId !== null && (
                <BookingDialog
                    idBooking={bookingId}
                    onUpdate={() => loadBookings()}
                    onClose={() => setBookingId(null)}
                />
            )}
        </SttPage>
    );
}
